import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Heart, 
  RotateCcw, 
  Sparkles,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AIMatchCard from './AIMatchCard';
import TipModal from './TipModal';
import MatchCelebration from './MatchCelebration';
import api from '../services/api';
import { UpgradeSheet, LikesLeftChip } from './UpgradePrompt';

const EnhancedSwipeCards = ({ onViewMatches, onOpenChat, onUpgrade }) => {
  const [matches, setMatches] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTipModal, setShowTipModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [swipeHistory, setSwipeHistory] = useState([]);
  const [isSwipeMode, setIsSwipeMode] = useState(true); // Toggle between regular and AI enhanced
  const [allowance, setAllowance] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    loadMatches();
  }, [isSwipeMode]);

  const refreshAllowance = () => api.get('/swipes/like-allowance').then((r) => setAllowance(r.data.data)).catch(() => {});
  useEffect(() => { refreshAllowance(); }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      
      if (isSwipeMode) {
        // Load AI-enhanced matches
        response = await api.get('/ai-matching/enhanced-matches?limit=20');
        setMatches(response.data.matches || []);
      } else {
        // Load regular discovery (fallback)
        response = await api.get('/users/discovery');
        const users = response.data.data || [];
        
        // Transform to match AI format (server-computed heuristic compatibility)
        const transformedMatches = users.map(user => ({
          ...user,
          compatibility_score: Math.round(user.compatibility || 0),
          compatibility_explanation: "Based on shared interests, age and distance"
        }));
        
        setMatches(transformedMatches);
      }
      
      setCurrentIndex(0);
      
    } catch (err) {
      console.error('Load matches error:', err);
      
      // If AI matching fails, fall back to basic discovery before surfacing an error
      if (isSwipeMode) {
        setIsSwipeMode(false);
        return;
      }
      
      setError("We couldn't load profiles right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const recordSwipe = async (user, action, extra = {}) => {
    const res = await api.post('/swipes/', { target_user_id: user.id, action, ...extra });
    const data = res.data?.data || {};
    setSwipeHistory(prev => [...prev, { user, direction: action === 'pass' ? 'left' : 'right', index: currentIndex, tip: extra.tip_offer }]);
    setCurrentIndex(prev => prev + 1);
    if (data.is_match) {
      setCelebration({ matchId: data.match_id, name: data.match_user?.name || user.name, image: data.match_user?.image || user.images?.[0] || null });
    } else if (action === 'pass') {
      toast({ title: "Passed", description: `Moved on from ${user.name}` });
    } else {
      toast({
        title: extra.tip_offer ? "Like with Tip Sent! 💝" : "Liked! 💖",
        description: extra.tip_offer ? `You liked ${user.name} with a $${extra.tip_offer} tip offer.` : `You liked ${user.name}. Hope it's a match!`,
      });
    }
    return data;
  };

  const swipeErrorToast = (err, title) => {
    if (err?.response?.status === 402) {
      refreshAllowance();
      setShowUpgrade(true);
      return;
    }
    const detail = err?.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(' ') : 'Please try again.';
    toast({ title, description: msg, variant: "destructive" });
  };

  const handleSwipe = async (user, direction) => {
    if (direction === 'right') {
      setSelectedUser(user);
      setShowTipModal(true);
      return;
    }
    try {
      await recordSwipe(user, 'pass');
    } catch (err) {
      if (err?.response?.status === 400 && String(err.response.data?.detail).includes('Already swiped')) {
        setCurrentIndex(prev => prev + 1);
        return;
      }
      swipeErrorToast(err, "Swipe Failed");
    }
  };

  const handleLikeWithoutTip = async () => {
    const user = selectedUser;
    setShowTipModal(false);
    setSelectedUser(null);
    try {
      await recordSwipe(user, 'like');
      refreshAllowance();
    } catch (err) {
      if (err?.response?.status === 400 && String(err.response.data?.detail).includes('Already swiped')) {
        setCurrentIndex(prev => prev + 1);
        return;
      }
      swipeErrorToast(err, "Like Failed");
    }
  };

  const handleTipSubmit = async (tipData) => {
    const user = selectedUser;
    try {
      await recordSwipe(user, 'like', { tip_offer: tipData.amount, tip_message: tipData.message });
      refreshAllowance();
      setShowTipModal(false);
      setSelectedUser(null);
    } catch (err) {
      swipeErrorToast(err, "Tip Failed");
      throw err;
    }
  };

  const undoLastSwipe = () => {
    if (swipeHistory.length === 0) return;
    
    const lastSwipe = swipeHistory[swipeHistory.length - 1];
    setSwipeHistory(prev => prev.slice(0, -1));
    setCurrentIndex(lastSwipe.index);
    
    toast({
      title: "Undo Successful",
      description: `Undid ${lastSwipe.direction} swipe on ${lastSwipe.user.name}`,
    });
  };

  const refreshMatches = () => {
    setSwipeHistory([]);
    loadMatches();
  };

  // Get current match
  const currentMatch = matches[currentIndex];
  const hasMoreMatches = currentIndex < matches.length;

  const celebrationEl = celebration && (
    <MatchCelebration
      match={celebration}
      onClose={() => setCelebration(null)}
      onSayHi={() => { const id = celebration.matchId; setCelebration(null); onOpenChat && onOpenChat(id); }}
    />
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">
          {isSwipeMode ? 'Finding your best matches…' : 'Loading profiles…'}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-sm mx-auto mt-8" data-testid="discover-error">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2 w-full"
              onClick={refreshMatches}
              data-testid="discover-error-retry-btn"
            >
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!hasMoreMatches) {
    return (
      <div className="max-w-sm mx-auto mt-8" data-testid="discover-empty">
        {celebrationEl}
        <Card>
          <CardContent className="text-center py-12">
            <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">You're all caught up</h3>
            <p className="text-muted-foreground mb-4">
              There are no new profiles to discover right now. When someone you liked likes you back, they'll appear in your Matches.
            </p>
            <div className="space-y-2">
              <Button onClick={refreshMatches} className="w-full" data-testid="discover-refresh-btn">
                <RotateCcw className="h-4 w-4 mr-2" />
                Check for new profiles
              </Button>
              {onViewMatches && (
                <Button variant="outline" onClick={onViewMatches} className="w-full" data-testid="discover-view-matches-btn">
                  <Heart className="h-4 w-4 mr-2" />
                  View your matches
                </Button>
              )}
              {swipeHistory.length > 0 && (
                <Button 
                  variant="ghost" 
                  onClick={undoLastSwipe}
                  className="w-full"
                >
                  Undo Last Swipe
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm sm:max-w-md mx-auto px-2 sm:px-0" data-testid="discover-view">
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold">Discover</h2>
          {isSwipeMode && (
            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
              <Sparkles className="h-3 w-3 mr-1" />
              AI Enhanced
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {swipeHistory.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={undoLastSwipe}
              className="text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Undo
            </Button>
          )}
          
          <Badge variant="outline">
            {currentIndex + 1} / {matches.length}
          </Badge>
        </div>
      </div>
      {allowance && !allowance.unlimited && (
        <div className="flex justify-center mb-3">
          <button type="button" onClick={() => setShowUpgrade(true)} className="focus:outline-none" data-testid="likes-left-chip-btn">
            <LikesLeftChip allowance={allowance} />
          </button>
        </div>
      )}

      {/* Match Card */}
      {currentMatch && (
        <div className="relative">
          <AIMatchCard
            key={currentMatch.id}
            user={currentMatch}
            compatibilityData={{
              compatibility_score: currentMatch.compatibility_score,
              explanation: currentMatch.compatibility_explanation,
              shared_interests: currentMatch.shared_interests,
              complementary_traits: currentMatch.complementary_traits,
              relationship_potential: currentMatch.relationship_potential
            }}
            onSwipe={handleSwipe}
            className="mb-4"
          />
        </div>
      )}

      {/* AI Mode Toggle */}
      <div className="text-center mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsSwipeMode(!isSwipeMode)}
          className="text-muted-foreground"
        >
          {isSwipeMode ? 'Switch to Basic Mode' : 'Try AI Enhanced Mode'}
        </Button>
      </div>

      {/* Tip Modal */}
      {showTipModal && selectedUser && (
        <TipModal
          isOpen={showTipModal}
          userProfile={selectedUser}
          onSendTip={handleTipSubmit}
          onLikeOnly={handleLikeWithoutTip}
          onClose={() => {
            setShowTipModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {celebrationEl}

      <UpgradeSheet open={showUpgrade} onOpenChange={setShowUpgrade} allowance={allowance} onUpgrade={() => { setShowUpgrade(false); onUpgrade?.(); }} />
    </div>
  );
};

export default EnhancedSwipeCards;